'use client';

import {
  IconLogout,
  IconShield,
  IconUser,
  IconChevronDown,
  IconBrightness
} from '@tabler/icons-react';
import { Link } from '@/i18n/routing';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/auth-context';
import { useTheme } from 'next-themes';
import { useTranslations } from 'next-intl';

export function HeaderUser() {
  const { user, isAuthenticated, logout } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const t = useTranslations('navigation.userMenu');

  if (!isAuthenticated || !user) return null;

  const initials = user.email.split('@')[0].slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='flex items-center gap-2 px-2'>
          <Avatar className='h-8 w-8 rounded-full'>
            <AvatarFallback className='rounded-full text-xs'>
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className='hidden text-start text-sm leading-tight md:grid'>
            <span className='truncate font-semibold'>{user.name}</span>
            <span className='text-muted-foreground truncate text-xs'>
              {user.email}
            </span>
          </div>
          <IconChevronDown className='text-muted-foreground hidden size-4 md:block' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56 rounded-lg' align='end'>
        <DropdownMenuLabel className='p-0 font-normal'>
          <div className='flex items-center gap-2 px-2 py-1.5 text-start text-sm'>
            <Avatar className='h-8 w-8 rounded-full'>
              <AvatarFallback className='rounded-full text-xs'>
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className='grid flex-1 text-start text-sm leading-tight'>
              <span className='truncate font-semibold'>{user.name}</span>
              <span className='truncate text-xs'>{user.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link href='/dashboard/settings/account'>
              <IconUser className='me-2 h-4 w-4' />
              {t('account')}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href='/dashboard/settings/security'>
              <IconShield className='me-2 h-4 w-4' />
              {t('security')}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className='md:hidden' />
        <DropdownMenuItem
          className='md:hidden'
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        >
          <IconBrightness className='me-2 h-4 w-4' />
          {t('toggleTheme')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()}>
          <IconLogout className='me-2 h-4 w-4' />
          {t('logOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
